import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import {
  CampaignPipelineType,
  STANDARD_PIPELINE_TYPE,
  type CampaignPipelineDefinition,
} from "@/marketing/models/campaignPipeline";
import { getStageLabel } from "./campaignPipeline";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Stages that mean the same as their Standard namesake; Performance Seeding's Shortlisted still awaits the brand.
const STANDARD_MEANING_STAGES: Partial<Record<string, ReadonlySet<string>>> = {
  [CampaignPipelineType.PerformanceSeeding]: new Set([
    "Form Sent",
    "Shipped",
    "Received",
    "Published",
    "Completed",
  ]),
};
const DAYS_PER_MONTH = 30;

function formatRelative(iso: string, now: Date): string {
  const ms = now.getTime() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const days = Math.floor(ms / MS_PER_DAY);
  if (days < 1) return "today";
  if (days === 1) return "1d ago";
  if (days < DAYS_PER_MONTH) return `${days}d ago`;
  const months = Math.floor(days / DAYS_PER_MONTH);
  return `${months}mo ago`;
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

// Bespoke subtexts for the Standard stage names; every other stage uses the generic label + age line.
function standardStageSubtext(
  creator: CampaignInfluencer,
  now: Date,
): string | undefined {
  const draftCount = creator.contentUrls?.length ?? 0;
  switch (creator.pipelineStage) {
    case "Form Sent":
      return creator.joinedAt
        ? `Form sent · ${formatRelative(creator.joinedAt, now)}`
        : "Form sent";
    case "Shortlisted":
      return creator.lastTransitionAt
        ? `Shortlisted · ${formatRelative(creator.lastTransitionAt, now)}`
        : "Shortlisted";
    case "Shipped":
      return creator.shippingTracking
        ? `Tracking ${creator.shippingTracking}`
        : "Tracking pending";
    case "Received":
      return draftCount > 0
        ? `${pluralize(draftCount, "draft")} submitted`
        : "Awaiting draft";
    case "Published":
      return draftCount > 0
        ? `${pluralize(draftCount, "post")} live`
        : "Published";
    case "Completed":
      return "";
    default:
      return undefined;
  }
}

function sharesStandardMeaning(
  definition: CampaignPipelineDefinition,
  stage: string,
): boolean {
  if (definition.type === STANDARD_PIPELINE_TYPE) return true;
  return STANDARD_MEANING_STAGES[definition.type]?.has(stage) ?? false;
}

export function getCreatorCardSubtext(
  definition: CampaignPipelineDefinition,
  creator: CampaignInfluencer,
  now: Date = new Date(),
): string {
  if (creator.rejected) {
    return creator.rejectedAtStage
      ? `Rejected at ${creator.rejectedAtStage}`
      : "Rejected";
  }
  // Other pipelines may reuse a Standard stage name with another meaning, so the bespoke copy needs a match.
  if (sharesStandardMeaning(definition, creator.pipelineStage)) {
    const bespoke = standardStageSubtext(creator, now);
    if (bespoke !== undefined) return bespoke;
  }

  const label = getStageLabel(definition, creator.pipelineStage);
  const since = creator.lastTransitionAt ?? creator.joinedAt;
  return since ? `${label} · ${formatRelative(since, now)}` : label;
}
